import { Activity, ShieldCheck, ShieldX } from 'lucide-react';
import { useState, useRef } from 'react';

import { isoDateFromTimestamp } from '@/portainer/filters/filters';
import { Environment } from '@/react/portainer/environments/types';

import { EnvironmentStatusBadgeItem } from './EnvironmentStatusBadgeItem';

interface Props {
  showLastCheckInDate?: boolean;
  environment: Environment;
  showMTLSStatus?: boolean;
}

export function EdgeIndicator({
  environment,
  showLastCheckInDate = false,
  showMTLSStatus = false,
}: Props) {
  const MTLSStatus = environment.MTLSStatus ?? { enabled: false, ok: false };
  const [mtlsError, setMtlsError] = useState<string>('');
  const [showTooltip, setShowTooltip] = useState(false);
  const errorFetchedRef = useRef(false);

  async function fetchMTLSError(environmentId: number) {
    try {
      const response = await fetch(
        `/api/endpoints/${environmentId}/mtls_certificate_error`
      );
      if (response.ok) {
        const data = await response.json();
        setMtlsError(data.message || 'Unknown MTLS error');
      } else {
        setMtlsError('Failed to fetch MTLS error details');
      }
    } catch {
      setMtlsError('Error retrieving MTLS status information');
    }
  }

  function handleMouseEnter() {
    setShowTooltip(true);
    // Only fetch the error if MTLS is enabled but not OK and we haven't fetched it yet
    if (
      MTLSStatus.enabled &&
      !MTLSStatus.ok &&
      environment.Id &&
      !errorFetchedRef.current
    ) {
      fetchMTLSError(environment.Id);
      errorFetchedRef.current = true;
    }
  }

  function getMTLSIcon() {
    if (MTLSStatus.ok) {
      return (
        <ShieldCheck
          className="icon icon-sm space-right !text-green-7"
          aria-hidden="true"
        />
      );
    }

    return (
      <ShieldX
        className="icon icon-sm space-right cursor-pointer !text-error-7"
        aria-hidden="true"
      />
    );
  }

  const associated = !!environment.EdgeID;
  if (!associated) {
    return (
      <span role="status" aria-label="edge-status">
        <EnvironmentStatusBadgeItem aria-label="unassociated">
          <span className="whitespace-nowrap">Not associated</span>
        </EnvironmentStatusBadgeItem>
      </span>
    );
  }

  return (
    <span
      role="status"
      aria-label="edge-status"
      className="flex items-center gap-1"
    >
      {showMTLSStatus && MTLSStatus.enabled && (
        <div
          className="relative inline-flex"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <EnvironmentStatusBadgeItem
            color={MTLSStatus.ok ? 'success' : 'danger'}
            aria-label="edge-mtls"
          >
            {getMTLSIcon()}
            mTLS
          </EnvironmentStatusBadgeItem>
          {showTooltip && !MTLSStatus.ok && mtlsError && (
            <div
              className="bg-gray-900 pointer-events-none absolute bottom-0 left-0 z-50 mt-2 translate-y-full transform rounded-sm p-2 text-xs text-white opacity-100 shadow-lg"
              style={{
                minWidth: '200px',
                maxWidth: '300px',
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              {mtlsError}
            </div>
          )}
        </div>
      )}
      {showLastCheckInDate && !!environment.LastCheckInDate && (
        <span
          className="small text-muted vertical-center"
          aria-label="edge-last-checkin"
          title="Last edge check-in"
        >
          <Activity className="icon icon-sm space-right" aria-hidden="true" />
          {isoDateFromTimestamp(environment.LastCheckInDate)}
        </span>
      )}
    </span>
  );
}
